/**
 * LA NAVIGATION SUR TÉLÉPHONE — lot 133, maquettes validées le 01/11/2026.
 *
 * Sur un écran étroit, la rangée des sous-menus de la barre haute tient sur
 * dix lignes : elle est masquée (styles.css, bloc « VERSION TÉLÉPHONE ») et
 * remplacée par ceci :
 *
 *   - une BARRE EN BAS, cinq entrées : Accueil, Ordres, Planning, Journées,
 *     Menu — les quatre écrans qu'on ouvre le plus, à portée du pouce ;
 *   - un MENU PLEIN ÉCRAN derrière « Menu », qui porte TOUT le reste.
 *
 * LES ENTRÉES DU MENU NE SONT PAS RÉÉCRITES ICI : elles viennent des mêmes
 * sous-menus que la barre haute (`Application.tsx`). Un écran ajouté au menu de
 * l'ordinateur apparaît donc aussi sur le téléphone, sans qu'on y pense.
 *
 * Le composant est TOUJOURS rendu ; c'est la feuille de style qui le montre
 * sous 640 px et le cache au-dessus. Un seul arbre React pour les deux tailles :
 * tourner le téléphone ne perd pas l'écran ouvert.
 */

import { useEffect, useState } from 'react';
import type { SousMenu } from './modules.js';

export interface ProprietesNavigationTelephone {
  /** Clé du module courant (`accueil`, `exploitation`, `finance`, `parametres`). */
  readonly module: string;
  /** L'écran courant du module Exploitation. */
  readonly exploitation: string;
  /** L'écran courant du module Finance. */
  readonly finance: string;
  readonly sousMenuExploitation: readonly SousMenu[];
  readonly sousMenuFinance: readonly SousMenu[];
  /** Aller à un module, et à un écran de ce module s'il est donné. */
  readonly quandAller: (module: string, ecran?: string) => void;
  readonly societe: string;
  readonly utilisateur: { readonly nom: string; readonly role: string };
  readonly quandDeconnexion: () => void;
}

/** Les entrées de la barre du bas, hors « Menu ». */
const RACCOURCIS = [
  { cle: 'accueil', nom: 'Accueil', module: 'accueil', ecran: undefined },
  { cle: 'ordres', nom: 'Ordres', module: 'exploitation', ecran: 'ordres' },
  { cle: 'planning', nom: 'Planning', module: 'exploitation', ecran: 'planning' },
  { cle: 'journees', nom: 'Pilotage', module: 'exploitation', ecran: 'journees' },
] as const;

export function NavigationTelephone({
  module,
  exploitation,
  finance,
  sousMenuExploitation,
  sousMenuFinance,
  quandAller,
  societe,
  utilisateur,
  quandDeconnexion,
}: ProprietesNavigationTelephone) {
  const [menuOuvert, setMenuOuvert] = useState(false);

  // Échap referme le menu, comme les menus de la barre haute.
  useEffect(() => {
    if (!menuOuvert) return;
    const aLaTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOuvert(false);
    };
    document.addEventListener('keydown', aLaTouche);
    return () => document.removeEventListener('keydown', aLaTouche);
  }, [menuOuvert]);

  /** Le raccourci allumé ; aucun quand l'écran ouvert n'en a pas — « Menu » l'est alors. */
  const raccourciActif = RACCOURCIS.find((r) =>
    r.module === 'accueil'
      ? module === 'accueil'
      : module === r.module && exploitation === r.ecran,
  )?.cle;

  const aller = (cible: string, ecran?: string) => {
    setMenuOuvert(false);
    quandAller(cible, ecran);
  };

  const courant = (cible: string, ecran: string) =>
    module === cible && (cible === 'finance' ? finance : exploitation) === ecran;

  /** Une section du menu : les écrans directs en lignes, les groupes en pilules. */
  const section = (titre: string, cible: string, sousMenu: readonly SousMenu[]) => (
    <>
      <span className="titre-menu-tel">{titre}</span>
      {sousMenu.map((entree) =>
        entree.enfants === undefined ? (
          <button
            key={entree.cle}
            type="button"
            className="entree-menu-tel"
            aria-current={courant(cible, entree.cle) ? 'page' : undefined}
            onClick={() => aller(cible, entree.cle)}
          >
            {entree.nom}
          </button>
        ) : (
          <div key={entree.cle} className="groupe-menu-tel">
            <span className="nom-groupe-tel">{entree.nom}</span>
            <div className="pilules-menu-tel">
              {entree.enfants.map((enfant) => (
                <button
                  key={enfant.cle}
                  type="button"
                  className="pilule-menu-tel"
                  aria-current={courant(cible, enfant.cle) ? 'page' : undefined}
                  onClick={() => aller(cible, enfant.cle)}
                >
                  {enfant.nom}
                </button>
              ))}
            </div>
          </div>
        ),
      )}
    </>
  );

  return (
    <>
      <nav className="nav-telephone" aria-label="Navigation principale">
        {RACCOURCIS.map((r) => (
          <button
            key={r.cle}
            type="button"
            className="bouton-nav-tel"
            aria-current={raccourciActif === r.cle && !menuOuvert ? 'page' : undefined}
            onClick={() => aller(r.module, r.ecran)}
          >
            <Icone nom={r.cle} />
            {r.nom}
          </button>
        ))}
        <button
          type="button"
          className="bouton-nav-tel"
          aria-current={raccourciActif === undefined || menuOuvert ? 'page' : undefined}
          aria-expanded={menuOuvert}
          onClick={() => setMenuOuvert((o) => !o)}
        >
          <Icone nom="menu" />
          Menu
        </button>
      </nav>

      {menuOuvert && (
        <div className="menu-telephone" role="dialog" aria-label="Menu">
          <div className="tete-menu-tel">
            <span className="mot-appli">STAFA</span>
            <button
              type="button"
              className="fermer-menu-tel"
              aria-label="Fermer le menu"
              onClick={() => setMenuOuvert(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="identite-menu-tel">
            <span className="societe-barre">{societe}</span>
            <span className="personne-barre">
              {utilisateur.nom} · {utilisateur.role}
            </span>
          </div>
          <div className="corps-menu-tel">
            <button
              type="button"
              className="entree-menu-tel"
              aria-current={module === 'accueil' ? 'page' : undefined}
              onClick={() => aller('accueil')}
            >
              Accueil
            </button>
            {section('EXPLOITATION', 'exploitation', sousMenuExploitation)}
            {section('FINANCE', 'finance', sousMenuFinance)}
          </div>
          <div className="pied-menu-tel">
            <button
              type="button"
              className="entree-menu-tel"
              aria-current={module === 'parametres' ? 'page' : undefined}
              onClick={() => aller('parametres')}
            >
              Paramètres
            </button>
            <button type="button" className="entree-menu-tel discret" onClick={quandDeconnexion}>
              Se déconnecter
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Les dessins de la barre du bas — traits seuls, `aria-hidden` : le libellé est dessous. */
function Icone({ nom }: { readonly nom: string }) {
  const trait = {
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    fill: 'none',
  };
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      {nom === 'accueil' && (
        <>
          <path d="M3 11l9-7 9 7" {...trait} />
          <path d="M5 10v10h14V10" {...trait} />
        </>
      )}
      {nom === 'ordres' && <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" {...trait} />}
      {nom === 'planning' && (
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" {...trait} />
          <path d="M3 10h18M8 3v4M16 3v4" {...trait} />
        </>
      )}
      {nom === 'journees' && (
        <>
          <path d="M1 6h13v11H1zM14 10h4l3 3v4h-7" {...trait} />
          <circle cx="5.5" cy="18.5" r="1.8" {...trait} />
          <circle cx="17.5" cy="18.5" r="1.8" {...trait} />
        </>
      )}
      {nom === 'menu' && <path d="M4 7h16M4 12h16M4 17h16" {...trait} />}
    </svg>
  );
}
